import {
  type CreateOnboardingCaseRequest,
  type OnboardingCaseKind,
  type OnboardingTemplate,
  type OnboardingTemplateDetail,
  type OnboardingAssigneeUser,
  createOnboardingCaseRequestSchema,
} from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useState } from 'react';
import { type Resolver, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import { useMe } from '@/platform/auth/use-me';
import { ONBOARDING_CASE_KIND_LABEL } from '@/products/onboarding/features/onboarding-template-labels';
import { useOnboardingAssigneeOptions } from '@/products/onboarding/features/use-onboarding-assignee-options';
import {
  useCreateOnboardingCase,
  useStartOnboardingCase,
} from '@/products/onboarding/features/use-onboarding-cases';
import {
  useOnboardingTemplate,
  useOnboardingTemplates,
} from '@/products/onboarding/features/use-onboarding-templates';

const START_FAILED = 'Der Vorgang konnte nicht gestartet werden.';
const UNPUBLISHED = 'Nur veröffentlichte Vorlagen können gestartet werden.';

export function OnboardingCaseStartDialog({
  open,
  onOpenChange,
  onStarted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStarted: (id: string) => void;
}) {
  const me = useMe();
  const create = useCreateOnboardingCase();
  const start = useStartOnboardingCase();
  const [error, setError] = useState<string | null>(null);
  const [personQ, setPersonQ] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState<string | null>(() =>
    open ? crypto.randomUUID() : null,
  );

  const form = useForm<CreateOnboardingCaseRequest>({
    resolver: zodResolver(
      createOnboardingCaseRequestSchema,
    ) as Resolver<CreateOnboardingCaseRequest>,
    defaultValues: emptyValues(me.data?.id),
  });

  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setIdempotencyKey(crypto.randomUUID());
    } else {
      setIdempotencyKey(null);
      setError(null);
      setPersonQ('');
      form.reset(emptyValues(me.data?.id));
    }
  }

  const kind = useWatch({ control: form.control, name: 'kind' });
  const templateId = useWatch({ control: form.control, name: 'templateId' });
  const ownerUserId = useWatch({ control: form.control, name: 'ownerUserId' });
  const subjectUserId = useWatch({ control: form.control, name: 'subjectUserId' });
  const subjectFirstName = useWatch({ control: form.control, name: 'subjectFirstName' });
  const subjectLastName = useWatch({ control: form.control, name: 'subjectLastName' });
  const subjectEmail = useWatch({ control: form.control, name: 'subjectEmail' });
  const entryOn = useWatch({ control: form.control, name: 'entryOn' });
  const exitOn = useWatch({ control: form.control, name: 'exitOn' });
  const lastWorkingDay = useWatch({ control: form.control, name: 'lastWorkingDay' });

  const templates = useOnboardingTemplates(
    {
      page: 1,
      pageSize: 100,
      sortBy: 'name',
      sortOrder: 'asc',
      kind,
      isActive: true,
    },
    { enabled: open },
  );
  const templateDetail = useOnboardingTemplate(templateId ?? '', { enabled: open });
  const publishedTemplates = useMemo(
    () =>
      (templates.data?.items ?? []).filter(
        (item) => item.isActive && item.currentPublishedVersionId && item.kind === kind,
      ),
    [kind, templates.data?.items],
  );
  const resolvedTemplateId = isFilledUuid(templateId)
    ? templateId
    : publishedTemplates.length === 1
      ? publishedTemplates[0]!.id
      : undefined;
  const resolvedOwnerId = isFilledUuid(ownerUserId) ? ownerUserId : me.data?.id;
  const usesContact = kind === 'onboarding' && usesContactFields(templateDetail.data);
  const needsLastWorkingDay = templateNeedsLastWorkingDay(templateDetail.data);
  const options = useOnboardingAssigneeOptions(
    {
      page: 1,
      pageSize: 50,
      includeInactive: false,
      q: personQ || undefined,
    },
    { enabled: open },
  );
  const listedPeople = options.data?.users.items ?? [];
  const ownerPeople = ensureCurrentPerson(
    listedPeople,
    ownerUserId ?? me.data?.id,
    me.data?.displayName ?? null,
    undefined,
  );
  const subjectPeople = ensureCurrentPerson(listedPeople, undefined, null, subjectUserId);

  useEffect(() => {
    if (!open || isFilledUuid(form.getValues('templateId')) || publishedTemplates.length !== 1) {
      return;
    }
    form.setValue('templateId', publishedTemplates[0]!.id, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [form, open, publishedTemplates]);

  function closeDialog(next: boolean) {
    onOpenChange(next);
  }

  async function onConfirm(values: CreateOnboardingCaseRequest) {
    if (!idempotencyKey) {
      return;
    }
    setError(null);
    const body = toCreateBody(
      {
        ...values,
        templateId: resolvedTemplateId ?? values.templateId,
        ownerUserId: resolvedOwnerId ?? values.ownerUserId,
      },
      idempotencyKey,
      usesContact,
    );
    try {
      const created = await create.mutateAsync(body);
      if (created.status === 'draft') {
        try {
          await start.mutateAsync({
            id: created.id,
            body: {
              revision: created.revision,
              entryOn: body.entryOn,
              exitOn: body.exitOn,
              lastWorkingDay: body.lastWorkingDay,
            },
          });
        } catch (caught) {
          const message = caught instanceof ApiRequestError ? caught.message : START_FAILED;
          setError(message);
          toast.error(message);
          return;
        }
      }
      toast.success('Vorgang gestartet.');
      closeDialog(false);
      onStarted(created.id);
    } catch (caught) {
      const message = caught instanceof ApiRequestError ? caught.message : START_FAILED;
      setError(message);
      toast.error(message);
    }
  }

  const confirmDisabled =
    !idempotencyKey ||
    create.isPending ||
    start.isPending ||
    publishedTemplates.length === 0 ||
    !isFilledUuid(resolvedTemplateId) ||
    templateDetail.isLoading ||
    !isFilledUuid(resolvedOwnerId) ||
    (kind === 'onboarding' && !entryOn) ||
    (kind === 'offboarding' && !exitOn) ||
    (needsLastWorkingDay && !lastWorkingDay) ||
    (kind === 'offboarding' && !isFilledUuid(subjectUserId)) ||
    (kind === 'onboarding' &&
      usesContact &&
      !hasContact(subjectFirstName, subjectLastName, subjectEmail)) ||
    (kind === 'onboarding' && !usesContact && !isFilledUuid(subjectUserId));

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent className="flex max-h-[min(90vh,48rem)] flex-col gap-4 overflow-hidden sm:max-w-lg">
        <DialogHeader className="shrink-0">
          <DialogTitle>Vorgang starten</DialogTitle>
          <DialogDescription>
            Eine veröffentlichte Vorlage wird zuerst angelegt und danach gestartet.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex min-h-0 flex-1 flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (confirmDisabled) {
              return;
            }
            void onConfirm(form.getValues());
          }}
        >
          <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
            <div className="space-y-2">
              <Label htmlFor="onboarding-case-kind">Art</Label>
              <Select
                value={kind}
                onValueChange={(value) => {
                  const next = value as OnboardingCaseKind;
                  form.setValue('kind', next, { shouldValidate: true, shouldDirty: true });
                  form.setValue('templateId', '', { shouldDirty: true });
                  form.setValue('subjectUserId', undefined, { shouldDirty: true });
                  form.setValue('subjectFirstName', undefined, { shouldDirty: true });
                  form.setValue('subjectLastName', undefined, { shouldDirty: true });
                  form.setValue('subjectEmail', undefined, { shouldDirty: true });
                  form.setValue('entryOn', next === 'onboarding' ? null : undefined, {
                    shouldDirty: true,
                  });
                  form.setValue('exitOn', next === 'offboarding' ? null : undefined, {
                    shouldDirty: true,
                  });
                  form.setValue('lastWorkingDay', null, {
                    shouldDirty: true,
                  });
                }}
              >
                <SelectTrigger id="onboarding-case-kind" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="onboarding">
                    {ONBOARDING_CASE_KIND_LABEL.onboarding}
                  </SelectItem>
                  <SelectItem value="offboarding">
                    {ONBOARDING_CASE_KIND_LABEL.offboarding}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="onboarding-case-template">Vorlage</Label>
              <Select
                value={isFilledUuid(templateId) ? templateId : 'none'}
                onValueChange={(value) => {
                  if (value === 'none') {
                    return;
                  }
                  form.setValue('templateId', value, { shouldValidate: true, shouldDirty: true });
                }}
              >
                <SelectTrigger id="onboarding-case-template" className="w-full">
                  <SelectValue placeholder="Veröffentlichte Vorlage wählen" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" disabled>
                    Veröffentlichte Vorlage wählen
                  </SelectItem>
                  {publishedTemplates.length === 0 ? (
                    <SelectItem value="__empty" disabled>
                      {templates.isPending ? 'Vorlagen werden geladen …' : UNPUBLISHED}
                    </SelectItem>
                  ) : (
                    publishedTemplates.map((item) => (
                      <SelectItem key={item.id} value={item.id}>
                        {templateLabel(item)}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {publishedTemplates.length === 0 && !templates.isPending ? (
                <p className="text-sm text-muted-foreground">{UNPUBLISHED}</p>
              ) : null}
            </div>

            <PersonSelect
              id="onboarding-case-owner"
              label="Prozessverantwortlich"
              value={ownerUserId}
              people={ownerPeople}
              onChange={(value) =>
                form.setValue('ownerUserId', value, { shouldValidate: true, shouldDirty: true })
              }
            />

            {usesContact ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="onboarding-case-first">Vorname</Label>
                  <Input
                    id="onboarding-case-first"
                    value={subjectFirstName ?? ''}
                    onChange={(event) =>
                      form.setValue('subjectFirstName', event.target.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="onboarding-case-last">Nachname</Label>
                  <Input
                    id="onboarding-case-last"
                    value={subjectLastName ?? ''}
                    onChange={(event) =>
                      form.setValue('subjectLastName', event.target.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="onboarding-case-email">E-Mail</Label>
                  <Input
                    id="onboarding-case-email"
                    type="email"
                    value={subjectEmail ?? ''}
                    onChange={(event) =>
                      form.setValue('subjectEmail', event.target.value, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  />
                </div>
              </>
            ) : (
              <PersonSelect
                id="onboarding-case-subject"
                label="Betroffene Person"
                value={subjectUserId}
                people={subjectPeople}
                onChange={(value) =>
                  form.setValue('subjectUserId', value, { shouldValidate: true, shouldDirty: true })
                }
              />
            )}

            <div className="space-y-2">
              <Label htmlFor="onboarding-case-person-q">Person suchen</Label>
              <Input
                id="onboarding-case-person-q"
                value={personQ}
                onChange={(event) => setPersonQ(event.target.value)}
                placeholder="Suche nach Name oder E-Mail"
              />
            </div>

            {kind === 'onboarding' ? (
              <div className="space-y-2">
                <Label htmlFor="onboarding-case-entry">Eintritt</Label>
                <Input
                  id="onboarding-case-entry"
                  type="date"
                  value={entryOn ?? ''}
                  onChange={(event) =>
                    form.setValue('entryOn', event.target.value || null, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                />
              </div>
            ) : null}
            {kind === 'onboarding' && needsLastWorkingDay ? (
              <div className="space-y-2">
                <Label htmlFor="onboarding-case-last-onboarding">Letzter Arbeitstag</Label>
                <Input
                  id="onboarding-case-last-onboarding"
                  type="date"
                  value={lastWorkingDay ?? ''}
                  onChange={(event) =>
                    form.setValue('lastWorkingDay', event.target.value || null, {
                      shouldValidate: true,
                      shouldDirty: true,
                    })
                  }
                />
              </div>
            ) : null}
            {kind === 'offboarding' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="onboarding-case-exit">Austritt</Label>
                  <Input
                    id="onboarding-case-exit"
                    type="date"
                    value={exitOn ?? ''}
                    onChange={(event) =>
                      form.setValue('exitOn', event.target.value || null, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="onboarding-case-last-day">Letzter Arbeitstag</Label>
                  <Input
                    id="onboarding-case-last-day"
                    type="date"
                    value={lastWorkingDay ?? ''}
                    onChange={(event) =>
                      form.setValue('lastWorkingDay', event.target.value || null, {
                        shouldValidate: true,
                        shouldDirty: true,
                      })
                    }
                  />
                </div>
              </>
            ) : null}
          </div>

          {options.isError ? (
            <p className="text-sm text-destructive" role="alert">
              {options.error instanceof ApiRequestError
                ? options.error.message
                : 'Die Auswahl konnte nicht geladen werden.'}
            </p>
          ) : null}

          {error ? (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          ) : null}

          <DialogFooter className="shrink-0">
            <Button type="button" variant="outline" onClick={() => closeDialog(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={confirmDisabled}>
              Starten
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PersonSelect({
  id,
  label,
  value,
  people,
  onChange,
}: {
  id: string;
  label: string;
  value: string | undefined;
  people: OnboardingAssigneeUser[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Select value={isFilledUuid(value) ? value : undefined} onValueChange={onChange}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder="Person wählen" />
        </SelectTrigger>
        <SelectContent>
          {people.length === 0 ? (
            <SelectItem value="__empty" disabled>
              Keine Treffer.
            </SelectItem>
          ) : (
            people.map((person) => (
              <SelectItem key={person.id} value={person.id}>
                {person.displayName}
                {person.email ? ` · ${person.email}` : ''}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

function emptyValues(ownerUserId: string | undefined): CreateOnboardingCaseRequest {
  return {
    kind: 'onboarding',
    templateId: '',
    ownerUserId: ownerUserId ?? '',
    subjectUserId: undefined,
    subjectFirstName: undefined,
    subjectLastName: undefined,
    subjectEmail: undefined,
    entryOn: null,
    exitOn: undefined,
    lastWorkingDay: undefined,
  };
}

function toCreateBody(
  values: CreateOnboardingCaseRequest,
  createIdempotencyKey: string,
  usesContact: boolean,
): CreateOnboardingCaseRequest {
  if (values.kind === 'offboarding') {
    return {
      kind: 'offboarding',
      templateId: values.templateId,
      ownerUserId: values.ownerUserId,
      subjectUserId: values.subjectUserId,
      exitOn: values.exitOn ?? null,
      lastWorkingDay: values.lastWorkingDay ?? null,
      createIdempotencyKey,
    };
  }
  if (usesContact) {
    return {
      kind: 'onboarding',
      templateId: values.templateId,
      ownerUserId: values.ownerUserId,
      subjectFirstName: values.subjectFirstName,
      subjectLastName: values.subjectLastName,
      subjectEmail: values.subjectEmail,
      entryOn: values.entryOn ?? null,
      lastWorkingDay: values.lastWorkingDay ?? null,
      createIdempotencyKey,
    };
  }
  return {
    kind: 'onboarding',
    templateId: values.templateId,
    ownerUserId: values.ownerUserId,
    subjectUserId: values.subjectUserId,
    entryOn: values.entryOn ?? null,
    lastWorkingDay: values.lastWorkingDay ?? null,
    createIdempotencyKey,
  };
}

function publishedDefinition(detail: OnboardingTemplateDetail | undefined) {
  if (!detail) {
    return undefined;
  }
  return detail.versions.find((item) => item.id === detail.currentPublishedVersionId)?.definition;
}

function usesContactFields(detail: OnboardingTemplateDetail | undefined): boolean {
  return Boolean(
    publishedDefinition(detail)?.phases.some((phase) => phase.stage === 'preboarding'),
  );
}

function templateNeedsLastWorkingDay(detail: OnboardingTemplateDetail | undefined): boolean {
  return Boolean(
    publishedDefinition(detail)?.phases.some((phase) =>
      phase.tasks.some((task) => task.anchor === 'last_working_day'),
    ),
  );
}

function hasContact(
  firstName: string | undefined,
  lastName: string | undefined,
  email: string | undefined,
): boolean {
  return Boolean(firstName?.trim() && lastName?.trim() && email?.trim());
}

function isFilledUuid(value: string | undefined): value is string {
  return value?.length === 36;
}

function templateLabel(item: OnboardingTemplate): string {
  return item.name;
}

function ensureCurrentPerson(
  items: OnboardingAssigneeUser[],
  ownerId: string | undefined,
  ownerLabel: string | null,
  subjectId: string | undefined,
): OnboardingAssigneeUser[] {
  let next = items;
  if (ownerId && !next.some((item) => item.id === ownerId)) {
    next = [placeholderPerson(ownerId, ownerLabel), ...next];
  }
  if (subjectId && !next.some((item) => item.id === subjectId)) {
    next = [placeholderPerson(subjectId, null), ...next];
  }
  return next;
}

function placeholderPerson(id: string, label: string | null): OnboardingAssigneeUser {
  return {
    id,
    email: '',
    displayName: label ?? id,
    isActive: true,
  };
}
